/**
 * ObjectTextField React component
 * 
 * @author    Maurizio Tidei, Jens Gruschel
 * @copyright © 2018 contexagon GmbH
 */


// React
import React from 'react';
import PropTypes from 'prop-types';
import connect from "react-redux/es/connect/connect";

// Material UI
import {withStyles} from "@material-ui/core";

// Material UI components
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Avatar from '@material-ui/core/Avatar';

// application
import {selectSupportedLanguages, selectTranslation} from "../../redux/configuration/selectors";
import {getCountryCodeForLanguage} from '../../utils';

// 3rd party
import _ from 'lodash';


/**
 * ObjectTextField is an editing component for text fields of objects
 *
 * @author Maurizio Tidei, Jens Gruschel
 */
export class ObjectTextField extends React.Component {

    state = {
        selectedLanguage: "de"
    };

    handleChange = (id, localizable) => event => {
        const value = event.target.value === "" ? undefined : event.target.value;
        if (localizable) {
            let obj = {...this.props.value, [this.state.selectedLanguage]: value};
            if (!value) delete obj[this.state.selectedLanguage];
            this.props.onObjectValueChange(id, obj);
        }
        else {
            this.props.onObjectValueChange(id, value);
        }
    };

    validateText(acceptMissing) {
        if (this.props.value == null || this.props.value === "") {
            return acceptMissing || !this.props.fieldDef.mandatory;
        }
        else {
            return true;
        }
    }

    render() {

        const { classes, fieldDef } = this.props;

        const rows = fieldDef.maxlength && fieldDef.maxlength > 100 ? _.clamp(
            Math.floor(fieldDef.maxlength / 200),
            2, 8
        ) : 1;

        const multiLine = rows > 1;

        const value = this.props.translateExplicitly(this.props.value, this.state.selectedLanguage);

        let inputLabelProps = {};
        let inputProps = {
            readOnly: this.props.readOnly
        };

        // add a select for the suffixes, if present
        /*if (fieldDef.suffixes) {
            inputProps.endAdornment = this.createSuffixAdornment();
        }
        else if (this.props.localizable) {
            inputProps.endAdornment = this.createLanguageAdornment();
        }*/

        return <TextField
            className={multiLine ? classes.multiline : classes.textField}
            id={fieldDef.id}
            label={fieldDef.name}
            value={value != null ? value : ""}
            disabled={this.props.readOnly} // TODO: make text selectable for copy/paste
            required={fieldDef.mandatory}
            error={!this.validateText(true)}
            onChange={this.handleChange(fieldDef.id, this.props.localizable)}
            margin="normal"
            multiline={multiLine}
            fullWidth={true}
            type="text"
            helperText={fieldDef.note}
            InputLabelProps={inputLabelProps}
            InputProps={inputProps}
            variant={multiLine ? "outlined" : "standard"}
            rows={rows}
            rowsMax={rows+5}
        />;
    }

    createSuffixAdornment() {

        const { classes, fieldDef } = this.props;

        return <InputAdornment position="end">
            <Select
                value={this.props.suffix || fieldDef.defaultsuffix || fieldDef.suffixes[0] || ''}
                disabled={this.props.readOnly}
                className={classes.suffix}
                disableUnderline={true}
                onChange={this.handleChange(fieldDef.id + '__suffix', false)}
            >
                {fieldDef.suffixes.map((suffix, i) => {
                    return <MenuItem key={suffix} value={suffix}>{suffix}</MenuItem>
                })}
            </Select>
        </InputAdornment>;
    }

    createLanguageAdornment() {

        const { classes } = this.props;

        return (
            <InputAdornment position="end" className={classes.languageAdornment}>
                <Select
                    value={this.state.selectedLanguage}
                    className={classes.langaugeSelect}
                    disableUnderline={true}
                    onChange={event => this.setState({selectedLanguage: event.target.value})}
                >
                    {this.props.languages.map((language, i) => {
                        const present = this.props.value instanceof Object ? this.props.value[language] : i === 0;
                        if (this.props.languageVisualization === "code") {
                            return <MenuItem key={language} value={language}>
                                <Avatar className={classes.languageCode} style={{opacity: present ? 1.0 : 0.3}}>{language.toUpperCase()}</Avatar>
                            </MenuItem>
                        }
                        else {
                            const code = getCountryCodeForLanguage(language);
                            return <MenuItem key={language} value={language}>
                                <Avatar src={"/icons/flags/1x1/" + code + ".svg"} className={classes.languageFlag} style={{opacity: present ? 1.0 : 0.3}}/>
                            </MenuItem>
                        }
                    })}
                </Select>
            </InputAdornment>
        );
    }
}

ObjectTextField.propTypes = {
    onObjectValueChange: PropTypes.func.isRequired,
    fieldDef: PropTypes.object.isRequired,
    objectId: PropTypes.string,
    readOnly: PropTypes.bool,
    value: PropTypes.any
};

const styles = {
    languageAdornment: {
        alignSelf: "start",
        marginTop: 4
    },
    langaugeSelect: {
        color: '#666666',
        paddingLeft: 8
    },
    languageFlag: {
        width: 24,
        height: 24
    },
    languageCode: {
        width: 24,
        height: 24,
        backgroundColor: '#ff0000',
        fontSize: 12
    },
    suffix: {
        color: '#666666'
    },
    multiline: {
        marginTop: 16
    },
    textField: {
        // red star for mandatory fields
        "& label span": {
            color: "#FF0000"
        }
    }
};

const mapStateToProps = (state, props) => {
    return {
        localizable: props.fieldDef.localizable && state.configuration.active.supportedLanguages,
        languages: selectSupportedLanguages(state.configuration),
        languageVisualization: state.configuration.active.languageVisualization,
        translateExplicitly: (value, language) => selectTranslation(state.configuration, value, language, false)
    };
};

export default connect(mapStateToProps)(withStyles(styles)(ObjectTextField));